import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useMemo, useRef } from 'react';
import {
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  getGetTasksByModuleVersionQueryKey,
  getGetTaskByModuleVersionQueryKey,
  useGetTasksByModuleVersion,
} from '../../api/endpoints/tasks/tasks';
import type { Task } from '../../api/models/task';
import { useEvaluateModuleSubmission } from '../../api/endpoints/submission-evaluation/submission-evaluation';
import {
  useUploadModuleVersionImage,
  useDeleteModuleVersionImage,
  useGenerateModuleVersionImage,
} from '../../api/endpoints/module-images/module-images';
import {
  useUpdateModuleVersion,
  useCreateModuleVersion,
  useDeleteModuleVersion,
  useDeleteModule,
  useSetModuleAutogradeOnSubmission,
  getGetModulesQueryKey,
  getGetModuleVersionQueryKey,
  useGetModuleVersion,
  useGetModules,
} from '../../api/endpoints/modules/modules';
import { useRequestModuleVersionApproval } from '../../api/endpoints/module-workflow/module-workflow';
import { useDialog } from '../../context/DialogContext';

/** A deleted module answers 404. Other failures stay on the normal retry path. */
function isNotFound(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

function dropModuleFromListCache(cached: unknown, moduleId: string): unknown {
  if (!cached || typeof cached !== 'object' || !('data' in cached)) return cached;
  const response = cached as { data?: { data?: { id?: string }[] } };
  const modules = response.data?.data;
  if (!Array.isArray(modules) || !modules.some((module) => module.id === moduleId)) return cached;
  return {
    ...response,
    data: {
      ...response.data,
      data: modules.filter((module) => module.id !== moduleId),
    },
  };
}

/**
 * The lecturer's module list is cached and does not refetch on focus. When the
 * open module has been deleted, drop it immediately and refetch the list so
 * the sidebar stops offering it.
 */
function refreshModuleListAfterNotFound(queryClient: QueryClient, moduleId: string) {
  queryClient.setQueryData(
    getGetModulesQueryKey(),
    (cached: unknown) => dropModuleFromListCache(cached, moduleId),
  );
  queryClient.invalidateQueries({ queryKey: getGetModulesQueryKey() });
}

/**
 * Hook for managing module tasks and evaluations in the lecturer context.
 * @param {Object} [params] - Optional parameters
 * @param {string[]} [params.includes] - Additional data to include in the response
 */
export const useLecturerModuleData = (
  moduleIdParam?: string,
  versionParam?: string,
  taskIdParam?: string,
  params: { includes?: string[] } = {},
) => {
  const { includes = ['resources'] } = params;
  const { showToastMessage } = useDialog();
  const queryClient = useQueryClient();

  // Use passed params
  const moduleId = moduleIdParam || null;
  const version = versionParam || null;
  const taskId = taskIdParam || null;

  // Get modules list data
  const { data: modules, isLoading: isModulesLoading } = useGetModules(undefined, {
    query: {
      select: (response) => response?.data?.data ?? [],
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  });

  // Find the module that carries the version history (usually the one marked as current)
  const currentModule = useMemo(() => {
    if (!moduleId || !modules) return null;
    // We look for any version of this module that has is_current flag,
    // or fallback to just the first one we find with the matching ID to get history
    return modules.find((m) => m.id === moduleId && m.isCurrent)
      || modules.find((m) => m.id === moduleId);
  }, [moduleId, modules]);

  // Get specific version data if needed
  const moduleVersionQueryKey = getGetModuleVersionQueryKey(moduleId || '', version || '');
  const includesString = includes.join(',');
  const tasksQueryKey = getGetTasksByModuleVersionQueryKey(moduleId || '', version || '', {
    include: includesString,
  });
  const modulesQueryKey = getGetModulesQueryKey();

  // A 404 is already in the cache when a second observer (the settings form)
  // mounts. Reading it here stops that mount, and later window focus, from
  // requesting the deleted module again.
  const openedModuleMissing = !!moduleId && (
    isNotFound(queryClient.getQueryState(moduleVersionQueryKey)?.error)
    || isNotFound(queryClient.getQueryState(tasksQueryKey)?.error)
  );
  const missingModuleQuery = openedModuleMissing
    ? { retryOnMount: false as const, refetchOnWindowFocus: false, refetchOnReconnect: false }
    : {};

  const { data: moduleVersion, isLoading: isModuleVersionLoading, error: moduleVersionError } = useGetModuleVersion(
    moduleId || '',
    version || '',
    {}, // no params
    {
      query: {
        enabled: !!moduleId && !!version,
        staleTime: 1000 * 60 * 60, // 1 hour
        retry: (failureCount, error) => !isNotFound(error) && failureCount < 1,
        ...missingModuleQuery,
        select: (response) => response?.data?.data,
      },
    },
  );

  const {
    data: tasks,
    isLoading: isTasksLoading,
    isError,
    error,
  } = useGetTasksByModuleVersion(
    moduleId || '',
    version || '',
    {
      include: includesString,
    },
    {
      query: {
        enabled: !!moduleId && !!version,
        staleTime: 1000 * 60 * 60, // 1 hour
        retry: (failureCount, error) => !isNotFound(error) && failureCount < 1,
        ...missingModuleQuery,
        select: (response) => response?.data?.data || [],
      },
    },
  );

  const moduleNotFound = isNotFound(moduleVersionError) || isNotFound(error);
  const refreshedMissingModuleId = useRef<string | null>(null);
  useEffect(() => {
    if (!moduleId || !moduleNotFound) return;
    if (refreshedMissingModuleId.current === moduleId) return;
    refreshedMissingModuleId.current = moduleId;
    refreshModuleListAfterNotFound(queryClient, moduleId);
  }, [moduleId, moduleNotFound, queryClient]);

  const selectedTask = useMemo(() => {
    if (!taskId || !tasks) return null;
    return tasks.find((t) => t.id === taskId);
  }, [taskId, tasks]);

  // Module mutations
  const updateModuleMutation = useUpdateModuleVersion({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: moduleVersionQueryKey });
        queryClient.invalidateQueries({ queryKey: modulesQueryKey });
      },
    },
  });

  const setAutogradeOnSubmissionMutation = useSetModuleAutogradeOnSubmission({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: moduleVersionQueryKey });
        queryClient.invalidateQueries({ queryKey: modulesQueryKey });
      },
    },
  });

  const requestApprovalMutation = useRequestModuleVersionApproval({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: modulesQueryKey });
        queryClient.invalidateQueries({ queryKey: moduleVersionQueryKey });
      },
    },
  });

  const uploadImageMutation = useUploadModuleVersionImage({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: moduleVersionQueryKey });
        queryClient.invalidateQueries({ queryKey: modulesQueryKey });
      },
    },
  });

  const deleteImageMutation = useDeleteModuleVersionImage({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: moduleVersionQueryKey });
        queryClient.invalidateQueries({ queryKey: modulesQueryKey });
      },
    },
  });

  const generateImageMutation = useGenerateModuleVersionImage({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: moduleVersionQueryKey });
        queryClient.invalidateQueries({ queryKey: modulesQueryKey });
      },
    },
  });

  // New: Create module version mutation
  const createVersionMutation = useCreateModuleVersion({
    mutation: {
      onSuccess: (data) => {
        // Invalidate queries
        const newVersion = data?.data?.data?.version;
        queryClient.invalidateQueries({ queryKey: modulesQueryKey });
        queryClient.invalidateQueries({ queryKey: getGetModuleVersionQueryKey(moduleId, newVersion) });
      },
    },
  });

  const deleteVersionMutation = useDeleteModuleVersion({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: moduleVersionQueryKey });
        queryClient.invalidateQueries({ queryKey: modulesQueryKey });
        queryClient.invalidateQueries({ queryKey: tasksQueryKey });
      },
    },
  });

  const deleteModuleMutation = useDeleteModule({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: modulesQueryKey });
      },
    },
  });

  // Task mutations
  const createTaskMutation = useCreateTask({
    mutation: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onMutate: async (variables: any) => {
        await queryClient.cancelQueries({ queryKey: tasksQueryKey });
        const previousTasks = queryClient.getQueryData(tasksQueryKey);
        const optimisticTask = {
          ...variables.data,
          id: variables.tempId || `temp-${Date.now()}`,
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueryData(tasksQueryKey, (old: any) => {
          const oldData = old?.data?.data || [];
          return {
            ...old,
            data: {
              ...(old?.data || {}),
              data: [...oldData, optimisticTask],
            },
          };
        });

        return { previousTasks, optimisticTask };
      },
      onError: (err, variables, context) => {
        if (context?.previousTasks) {
          queryClient.setQueryData(tasksQueryKey, context.previousTasks);
        }
        showToastMessage('error', 'Failed to create task');
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey: tasksQueryKey });
      },
    },
  });

  const updateTaskMutation = useUpdateTask({
    mutation: {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: tasksQueryKey });
        const previousTasks = queryClient.getQueryData(tasksQueryKey);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueryData(tasksQueryKey, (old: any) => {
          if (!old?.data?.data) return old;

          const updatedTasks = [...old.data.data];
          const taskIndex = updatedTasks.findIndex((t) => t.id === variables.taskId);

          if (taskIndex === -1) return old;

          // If we're updating sortIndex, we need to handle reordering
          if (variables.data.sortIndex !== undefined) {
            const currentTask = updatedTasks[taskIndex];
            const targetIndex = updatedTasks.findIndex(
              (t) => t.level === currentTask.level && t.sortIndex === variables.data.sortIndex && t.id !== currentTask.id,
            );

            // If there's a task with the target sortIndex, swap them
            if (targetIndex !== -1) {
              const targetTask = updatedTasks[targetIndex];
              updatedTasks[targetIndex] = {
                ...targetTask,
                sortIndex: currentTask.sortIndex,
              };
            }
          }

          // Update the task with new data
          updatedTasks[taskIndex] = {
            ...updatedTasks[taskIndex],
            ...variables.data,
            // Preserve nested objects that might not be included in the update
            evaluation: variables.data.evaluation
              ? { ...updatedTasks[taskIndex].evaluation, ...variables.data.evaluation }
              : updatedTasks[taskIndex].evaluation,
            // For rubric and analysis we honour an explicit override from the
            // outgoing PATCH: if the field is present in `variables.data` we
            // REPLACE it (not merge), because the form is the source of truth
            // and a shallow merge would keep stale nested arrays after deletes.
            // Only when the field key is absent from the request do we fall
            // back to the previously cached value.
            rubric: 'rubric' in variables.data
              ? variables.data.rubric
              : updatedTasks[taskIndex].rubric,
            analysis: 'analysis' in variables.data
              ? variables.data.analysis
              : updatedTasks[taskIndex].analysis,
            // mcqConfig is NOT part of the task update request payload — it is saved via a
            // separate PATCH /mcq endpoint. Preserve the existing cached mcqConfig here so
            // the optimistic update does NOT overwrite it with undefined/null.
            mcqConfig: updatedTasks[taskIndex].mcqConfig,
          };

          // Sort tasks by level and sortIndex to match API behavior
          updatedTasks.sort((a, b) => {
            if (a.level !== b.level) return a.level - b.level;
            return a.sortIndex - b.sortIndex;
          });

          return {
            ...old,
            data: {
              ...old.data,
              data: updatedTasks,
            },
          };
        });

        return { previousTasks };
      },
      onError: (err, variables, context) => {
        if (context?.previousTasks) {
          queryClient.setQueryData(tasksQueryKey, context.previousTasks);
        }
      },
      onSuccess: async (response, variables) => {
        const savedTask = response?.data?.data as Task | undefined;
        const reminted = !!savedTask && (
          savedTask.id !== variables.taskId || !!savedTask.remintedFromTaskId
        );

        if (reminted && savedTask && moduleId && version) {
          queryClient.setQueryData(tasksQueryKey, (old: unknown) => {
            const cached = old as { data?: { data?: Task[] } } | undefined;
            if (!cached?.data?.data) return old;

            const withoutOld = cached.data.data.filter((t) => t.id !== variables.taskId);
            const existingNewIndex = withoutOld.findIndex((t) => t.id === savedTask.id);
            const nextTasks = existingNewIndex === -1
              ? [...withoutOld, savedTask]
              : withoutOld.map((t, index) => (index === existingNewIndex ? savedTask : t));

            nextTasks.sort((a, b) => {
              if (a.level !== b.level) return a.level - b.level;
              return a.sortIndex - b.sortIndex;
            });

            return {
              ...cached,
              data: {
                ...cached.data,
                data: nextTasks,
              },
            };
          });

          await queryClient.invalidateQueries({ queryKey: tasksQueryKey });
          await queryClient.invalidateQueries({
            queryKey: getGetTaskByModuleVersionQueryKey(moduleId, version, variables.taskId),
          });
          await queryClient.invalidateQueries({
            queryKey: getGetTaskByModuleVersionQueryKey(moduleId, version, savedTask.id),
          });
          return;
        }

        await queryClient.invalidateQueries({ queryKey: tasksQueryKey });
      },
    },
  });

  const deleteTaskMutation = useDeleteTask({
    mutation: {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: tasksQueryKey });
        const previousTasks = queryClient.getQueryData(tasksQueryKey);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueryData(tasksQueryKey, (old: any) => {
          const oldData = old?.data?.data || [];
          return {
            ...old,
            data: {
              ...(old?.data || {}),
              data: oldData.filter((task: { id: string }) => task.id !== variables.taskId),
            },
          };
        });

        return { previousTasks };
      },
      onError: (err, variables, context) => {
        queryClient.setQueryData(tasksQueryKey, context.previousTasks);
        showToastMessage('error', 'Error deleting task');
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey: tasksQueryKey });
      },
    },
  });

  // Evaluation mutation
  const evaluateModuleSubmissionMutation = useEvaluateModuleSubmission();

  return {
    // Data
    module: moduleVersion,
    isModuleLoading: isModuleVersionLoading,
    tasks,
    selectedTask, // selected Task
    isLoading: isModuleVersionLoading || isTasksLoading,
    isError,
    error,
    /** The open module or its tasks answered 404: it was deleted since the list was cached. */
    moduleNotFound,

    // Current module data from list
    currentModule,
    versionHistory: currentModule?.versionHistory || {},

    // Modules list data
    modules,
    isModulesLoading,

    // Query keys for external use
    tasksQueryKey,
    modulesQueryKey,
    moduleVersionQueryKey,

    // Module operations
    updateModule: (data) => updateModuleMutation.mutateAsync({
      moduleId,
      moduleVersion: version,
      data,
    }),
    uploadImage: (file) => uploadImageMutation.mutateAsync({
      moduleId,
      version,
      data: { file },
    }),
    deleteImage: () => deleteImageMutation.mutateAsync({
      moduleId,
      version,
    }),
    generateImage: () => generateImageMutation.mutateAsync({
      moduleId,
      version,
    }),

    // Module Mutations
    updateModuleMutation,
    setAutogradeOnSubmissionMutation,
    requestApprovalMutation,
    uploadImageMutation,
    deleteImageMutation,
    generateImageMutation,
    createVersionMutation,
    deleteVersionMutation,
    deleteModuleMutation,

    // Task mutations
    createTaskMutation,
    updateTaskMutation,
    deleteTaskMutation,

    // Evaluation operations
    evaluateModuleSubmissionMutation,
  };
};

export default useLecturerModuleData;
